import React, { useState, useEffect, useCallback } from 'react';
import { FaSearch, FaTimes, FaDownload, FaSortUp, FaSortDown } from 'react-icons/fa';
import Pagination from '../components/Pagination';
import DateRangeFilter from '../components/DateRangeFilter';
import { fetchAccountsList, exportAccountsCsv } from '../services/api';
import { formatCompactNumber } from '../utils/formatters';
import '../components/DataTable.css';
import './DataPages.css';
import './Pages.css';

const toUTCDateStr = (date) => `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;

const getDefaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setUTCDate(start.getUTCDate() - 29);
  return {
    startDate: toUTCDateStr(start),
    endDate: toUTCDateStr(end)
  };
};

const AccountsDetails = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterActive, setFilterActive] = useState('');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [startDate, setStartDate] = useState(getDefaultDateRange().startDate);
  const [endDate, setEndDate] = useState(getDefaultDateRange().endDate);
  const [exporting, setExporting] = useState(false);
  const pageSize = 100;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await fetchAccountsList(page, pageSize, sortOrder, searchTerm, filterActive, startDate, endDate);
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, sortOrder, searchTerm, filterActive, startDate, endDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPage(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSearchClear = () => {
    setSearchInput('');
    setSearchTerm('');
    setPage(1);
  };

  const handleActiveChange = (e) => {
    setFilterActive(e.target.value);
    setPage(1);
  };

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'DESC' ? 'ASC' : 'DESC');
    setPage(1);
  };

  const handleDateApply = () => {
    setPage(1);
  };

  const handleDownloadCsv = async () => {
    try {
      setExporting(true);
      const response = await exportAccountsCsv(sortOrder, searchTerm, filterActive, startDate, endDate);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const now = new Date();
      const timestamp = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}_${String(now.getHours()).padStart(2,'0')}h${String(now.getMinutes()).padStart(2,'0')}m${String(now.getSeconds()).padStart(2,'0')}s`;
      const filename = `Accounts_${timestamp}.csv`;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const items = data ? data.items : [];

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Accounts</h1>
        </div>
      </div>

      <div className="data-page-content">
        {error && <div className="table-empty">Error: {error}</div>}

        {!error && (
          <>
            <div className="filters-container">
              <DateRangeFilter
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
                onApply={handleDateApply}
                earliestDate={data?.earliestDate}
                latestDate={data?.latestDate}
              />
            </div>

            <div className="data-page-controls">
              <div className="table-search-wrapper">
                <FaSearch className="table-search-icon" size={14} />
                <input
                  type="text"
                  className="table-search-input"
                  placeholder="Search..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                {searchInput && (
                  <button className="table-search-clear" onClick={handleSearchClear}>
                    <FaTimes size={14} />
                  </button>
                )}
                <button className="table-search-btn" onClick={handleSearch} title="Search">
                  <FaSearch size={14} />
                </button>
              </div>

              <div className="data-page-filters">
                <select
                  value={filterActive}
                  onChange={handleActiveChange}
                  className="data-page-filter-select"
                >
                  <option value="">All Status</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>

              <button className="btn-primary" onClick={handleDownloadCsv} title="Download CSV" disabled={exporting}>
                <FaDownload size={14} />
                <span>{exporting ? 'Exporting ...' : 'CSV'}</span>
              </button>

              {data && (
                <div className="control-badge" style={{ marginLeft: 'auto' }}>
                  <span className="badge-label">Viewing</span>
                  <span className="badge-value">{formatCompactNumber(data.totalCount)}</span>
                </div>
              )}
            </div>

            {loading && <div className="table-loading">Loading ...</div>}

            {!loading && items.length > 0 && (
              <div className="data-page-table-card">
                <div className="data-table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>User ID</th>
                        <th className="sortable" onClick={toggleSortOrder}>
                          Creation Date
                          <span className="sort-indicator">
                            {sortOrder === 'ASC' ? <FaSortUp size={12} /> : <FaSortDown size={12} />}
                          </span>
                        </th>
                        <th>First Name</th>
                        <th>Last Name</th>
                        <th>Email</th>
                        <th>Active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((account) => (
                        <tr key={account.userId}>
                          <td>{account.userId}</td>
                          <td>{formatDate(account.creationDate)}</td>
                          <td className="truncate" title={account.firstName || ''}>{account.firstName || '-'}</td>
                          <td className="truncate" title={account.lastName || ''}>{account.lastName || '-'}</td>
                          <td className="truncate" title={account.email || ''}>{account.email || '-'}</td>
                          <td>
                            <span className={`status-badge ${account.active ? 'status-active' : 'status-inactive'}`}>
                              {account.active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <Pagination
                  page={data.page}
                  totalPages={data.totalPages}
                  totalCount={data.totalCount}
                  pageSize={data.pageSize}
                  onPageChange={setPage}
                />
              </div>
            )}

            {!loading && data && items.length === 0 && (
              <div className="table-empty">
                {searchTerm || filterActive || startDate || endDate
                  ? 'No results found for the current filters.'
                  : 'No accounts found.'}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AccountsDetails;
